import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { SseHttpError } from '@/services/sse'
import { askCopilotTurn, streamCopilotTurn } from './stream'
import { createCopilotSession, findPendingTurn, isPendingTurnRefusal, titleFromQuestion } from './session'
import { useSessionTranscript } from './history'
import { turnMetadata } from './turnMetadata'
import { emptyTurnState, reduceTurnEvent } from './turnReducer'

/**
 * One chat with the copilot.
 *
 * A chat is one server session, opened from the list or started fresh. The caller
 * keys this hook's component by the chat, so switching chats is a remount rather
 * than a reset — clearing in place would render one frame of the previous chat
 * under the next one's title.
 *
 * `sessionId` is null for a new chat. Nothing is created until the first question,
 * because no endpoint can delete a session and an eager create would leave an
 * empty one in the list every time the dock was opened and closed. The session is
 * created with a title taken from that question, and reported through `onSession`
 * so the shell knows which chat is current.
 *
 * The assistant message is created empty and filled as frames arrive, so the
 * working, the answer and its sources appear in the order the engine produced
 * them — which matters more here than anywhere else in the app, because an
 * analysis can legitimately take six minutes.
 *
 * `onTurnEnd` fires once a turn has finished, with what the server's dispatcher
 * actually did. The copilot WRITES as well as reads, so a turn can change the page
 * the dock is sitting over.
 */

/**
 * Drops a trailing turn that produced nothing at all.
 *
 * An assistant message with no text, no error and no stage is a turn that was
 * aborted before it got anywhere — which is exactly what StrictMode does on mount,
 * and what killed the auto-asked Investigate turn. The user message that opened it
 * goes with it, rather than leaving a question with a blank answer under it.
 */
function withoutDeadTurn(messages) {
  const last = messages[messages.length - 1]
  if (!last || last.role !== 'assistant') return messages
  if (last.text || last.error || last.stages?.length) return messages
  return messages.slice(0, -2)
}

/** A turn the server will never answer, recognised from the stream rather than from a status. */
const isUnreadable = (state) => Boolean(state.error) && !state.text && state.stages.length === 0

export function useCopilotConversation({ sessionId: initialSessionId = null } = {}, { onSession, onTurnEnd } = {}) {
  const [live, setLive] = useState([])
  const [isStreaming, setStreaming] = useState(false)
  /**
   * The anomaly an analysis is being written for, while one is.
   *
   * Separate from `isStreaming` because the screen behind the dock cares about
   * exactly one of the two: an analysis is the only turn that writes to the record
   * that screen is displaying, and the only one that runs long enough to be worth
   * showing there.
   */
  const [analysingId, setAnalysingId] = useState(null)
  const abort = useRef(null)
  const session = useRef(initialSessionId)

  /**
   * An opened chat's transcript, DERIVED onto the front of the live turns rather
   * than copied into state — seeding from an effect calls setState on commit, the
   * cascading-render rule this codebase has already been bitten by.
   */
  const transcript = useSessionTranscript(initialSessionId)
  const replayed = transcript.data?.messages
  const messages = useMemo(() => (replayed?.length ? [...replayed, ...live] : live), [replayed, live])

  /**
   * Whether this chat can take a question.
   *
   * Only an OPENED chat can be blocked, and only until something is asked in it
   * here: the server refuses a turn while the last one has no answer, and a chat
   * whose last answer failed is refused for ever. Saying so up front beats letting
   * the reader type a question and lose it to a refusal.
   */
  const blocked = live.length === 0 ? (transcript.data?.blocked ?? 'none') : 'none'

  // Held in refs so a caller passing inline functions does not change `ask`'s
  // identity on every render — the auto-asked Investigate turn fires on it.
  const turnEnd = useRef(onTurnEnd)
  const sessionReport = useRef(onSession)
  useEffect(() => {
    turnEnd.current = onTurnEnd
    sessionReport.current = onSession
  }, [onTurnEnd, onSession])

  // Abort on unmount, which is also how a change of chat is handled.
  useEffect(() => () => abort.current?.abort(), [])

  const cancel = useCallback(() => abort.current?.abort(), [])

  /**
   * Ask.
   *
   * @param question the message, already composed (citations and all)
   * @param options.mentions {module, module_id}[] — what the question is about
   * @param options.route    `metadata.route`
   * @param options.actions  requested actions; `request_rca` starts an analysis
   * @param options.analysing the anomaly an analysis was requested for, if any
   */
  const ask = useCallback(
    async (question, { mentions = [], route, actions = [], analysing = null } = {}) => {
      const text = question.trim()
      if (!text || isStreaming) return

      abort.current?.abort()
      const controller = new AbortController()
      abort.current = controller
      const signal = controller.signal

      const localId = crypto.randomUUID()
      setLive((current) => [
        ...withoutDeadTurn(current),
        { id: `${localId}-user`, role: 'user', text },
        { id: localId, role: 'assistant', ...emptyTurnState, notice: null },
      ])
      setStreaming(true)
      setAnalysingId(analysing)

      const patch = (change) =>
        setLive((current) => current.map((message) => (message.id === localId ? { ...message, ...change } : message)))

      /* Accumulated HERE as well as in state: a setState updater must be pure, and
         the finally block below needs the turn's actions after the component may
         have unmounted. */
      let state = { ...emptyTurnState }
      const onEvent = (event) => {
        const change = reduceTurnEvent(state, event)
        if (!change) return
        state = { ...state, ...change }
        patch(change)
      }

      const startSession = async () => {
        const created = await createCopilotSession(titleFromQuestion(text), signal)
        session.current = created?.id ?? null
        sessionReport.current?.(created)
        return session.current
      }

      /**
       * Send, and deal with the one refusal that is routine.
       *
       * The server refuses a turn while the session's last question has no answer —
       * a 400 naming `session_id`. A turn that FAILED leaves a session in exactly
       * that state permanently, and a reload during a long analysis lands in it too.
       *
       * So: attach to the pending turn if it can still produce something, and
       * otherwise move the question to a new chat. Once, and counted — a server
       * refusing every session would otherwise loop here for ever.
       */
      const send = async (sessionId, metadata, attempt = 0) => {
        try {
          await askCopilotTurn({ sessionId, message: text, metadata, signal, onEvent })
          return
        } catch (error) {
          if (!isPendingTurnRefusal(error) || attempt > 0) throw error
        }

        const pending = await findPendingTurn(sessionId, signal)

        if (pending.state === 'attachable') {
          /* Whatever was written before we attached is gone — nothing is persisted
             mid-stream — so the answer may start mid-thought. The panel says so. */
          patch({ notice: 'reattached' })
          try {
            await streamCopilotTurn({ turnId: pending.turn.id, signal, onEvent })
            /* `stream unavailable` arrives as a frame, not a rejection: the turn was
               reaped between the refusal and this request. Fall through and rotate. */
            if (!isUnreadable(state)) return
          } catch (error) {
            if (!(error instanceof SseHttpError) || error.status !== 404) throw error
          }
        }

        const next = await startSession()
        state = { ...emptyTurnState }
        patch({ ...emptyTurnState, notice: 'rotated' })
        await send(next, metadata, attempt + 1)
      }

      try {
        const sessionId = session.current ?? (await startSession())
        await send(sessionId, turnMetadata({ mentions, route, actions }))
      } catch (error) {
        /* An aborted turn is the reader cancelling or leaving, not a failure to
           report. Tested on the SIGNAL: the fetch behind the stream throws a DOM
           `AbortError` while axios throws an `ApiError`, and matching on names
           caught only one of them. */
        if (!signal.aborted) patch({ error: error?.messageKey ?? 'unknown' })
      } finally {
        // Cleared by whichever turn is still the current one, aborted or not —
        // `ask` refuses to start while it is set, so leaving it set ends the chat.
        if (abort.current === controller) {
          setStreaming(false)
          setAnalysingId(null)
        }
        if (!signal.aborted) turnEnd.current?.({ actions: state.actions, analysed: analysing })
      }
    },
    [isStreaming],
  )

  return {
    messages,
    ask,
    cancel,
    isStreaming,
    analysingId,
    blocked,
    isRestoring: Boolean(initialSessionId) && transcript.isPending,
    restoreFailed: transcript.isError,
  }
}
